import { useState } from 'react'
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  RowSelectionState,
} from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  useReactTable,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { MoreHorizontal, AlertTriangle } from 'lucide-react'
import {
  DataTableColumnHeader,
  DataTableFacetedFilter,
  DataTablePagination,
  DataTableToolbar,
} from '@/components/data-table'
import type {
  SpaceConflictTableRow,
  ConflictStatus,
  ConflictSeverity,
} from '@/types/space-conflicts'
import {
  CONFLICT_STATUS_LABELS,
  CONFLICT_STATUSES,
  CONFLICT_STATUS_VARIANTS,
  CONFLICT_SEVERITY_LABELS,
  CONFLICT_SEVERITIES,
  CONFLICT_SEVERITY_VARIANTS,
  formatDateTime,
  formatDate,
  formatSqft,
} from '@/types/space-conflicts'

interface SpaceConflictsTableProps {
  data: SpaceConflictTableRow[]
  isLoading: boolean
  onResolve: (id: string, unitCode: string | null) => void
}

/** Status filter options */
const statusFilterOptions = CONFLICT_STATUSES.map((status) => ({
  label: CONFLICT_STATUS_LABELS[status],
  value: status,
}))

/** Severity filter options */
const severityFilterOptions = CONFLICT_SEVERITIES.map((severity) => ({
  label: CONFLICT_SEVERITY_LABELS[severity],
  value: severity,
}))

export function SpaceConflictsTable({
  data,
  isLoading,
  onResolve,
}: SpaceConflictsTableProps) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [globalFilter, setGlobalFilter] = useState('')

  const columns: ColumnDef<SpaceConflictTableRow>[] = [
    {
      accessorKey: 'conflict_date',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Conflict Date" />
      ),
      cell: ({ row }) => {
        const date = row.getValue('conflict_date') as string
        return <span className="text-sm">{formatDate(date)}</span>
      },
    },
    {
      id: 'unit',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Unit" />
      ),
      cell: ({ row }) => {
        const unitCode = row.original.unit_code
        const locationName = row.original.location_name

        if (unitCode) {
          return (
            <div>
              <div className="font-medium">{unitCode}</div>
              {locationName && (
                <div className="text-xs text-muted-foreground">{locationName}</div>
              )}
            </div>
          )
        }

        return <span className="text-muted-foreground">-</span>
      },
    },
    {
      accessorKey: 'trigger_type',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Trigger Type" />
      ),
      cell: ({ row }) => {
        const triggerType = row.getValue('trigger_type') as string
        return <span className="text-sm capitalize">{triggerType.replace(/_/g, ' ')}</span>
      },
    },
    {
      accessorKey: 'severity',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Severity" />
      ),
      cell: ({ row }) => {
        const severity = row.getValue('severity') as ConflictSeverity
        return (
          <Badge variant={CONFLICT_SEVERITY_VARIANTS[severity]}>
            {CONFLICT_SEVERITY_LABELS[severity]}
          </Badge>
        )
      },
      filterFn: (row, id, value: string[]) => {
        if (!value || value.length === 0) return true
        return value.includes(row.getValue(id))
      },
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => {
        const status = row.getValue('status') as ConflictStatus
        return (
          <Badge variant={CONFLICT_STATUS_VARIANTS[status]}>
            {CONFLICT_STATUS_LABELS[status]}
          </Badge>
        )
      },
      filterFn: (row, id, value: string[]) => {
        if (!value || value.length === 0) return true
        return value.includes(row.getValue(id))
      },
    },
    {
      accessorKey: 'shortfall_sqft',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Shortfall" />
      ),
      cell: ({ row }) => {
        const sqft = row.getValue('shortfall_sqft') as number | null
        return <span className="text-sm">{formatSqft(sqft)}</span>
      },
    },
    {
      id: 'affected_count',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Affected" />
      ),
      cell: ({ row }) => {
        const affected = row.original.affected_transient_ids
        const count = affected ? affected.length : 0
        return (
          <span className="text-sm">
            {count} {count === 1 ? 'transient' : 'transients'}
          </span>
        )
      },
    },
    {
      accessorKey: 'detected_at',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Detected" />
      ),
      cell: ({ row }) => {
        const detected = row.getValue('detected_at') as string
        return <span className="text-xs text-muted-foreground">{formatDateTime(detected)}</span>
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => onResolve(row.original.id, row.original.unit_code)}
              disabled={row.original.status !== 'open'}
            >
              <AlertTriangle className="mr-2 h-4 w-4" />
              Resolve Conflict
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
      enableHiding: false,
    },
  ]

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const search = filterValue.toLowerCase()
      const unitCode = (row.original.unit_code || '').toLowerCase()
      const triggerType = (row.original.trigger_type || '').toLowerCase()

      return unitCode.includes(search) || triggerType.includes(search)
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 15,
      },
    },
  })

  // Export to CSV
  const handleExport = () => {
    const headers = [
      'Conflict Date',
      'Unit',
      'Location',
      'Trigger Type',
      'Severity',
      'Status',
      'Shortfall (sqft)',
      'Affected Transients',
      'Detected',
    ]
    const rows = table.getFilteredRowModel().rows.map((row) => [
      row.original.conflict_date,
      row.original.unit_code || '',
      row.original.location_name || '',
      row.original.trigger_type,
      CONFLICT_SEVERITY_LABELS[row.original.severity],
      CONFLICT_STATUS_LABELS[row.original.status],
      row.original.shortfall_sqft?.toString() || '',
      row.original.affected_transient_ids?.length?.toString() || '0',
      row.original.detected_at,
    ])

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `space_conflicts_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
  }

  return (
    <div className="space-y-4">
      {/* Toolbar with search, filters, and view options */}
      <DataTableToolbar
        table={table}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        filterPlaceholder="Search conflicts..."
        onExport={handleExport}
        exportLabel="Export CSV"
      >
        <DataTableFacetedFilter
          column={table.getColumn('status')}
          title="Status"
          options={statusFilterOptions}
        />
        <DataTableFacetedFilter
          column={table.getColumn('severity')}
          title="Severity"
          options={severityFilterOptions}
        />
      </DataTableToolbar>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="text-muted-foreground">
                    Loading space conflicts...
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="text-muted-foreground">
                    No space conflicts found.
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <DataTablePagination table={table} />
    </div>
  )
}
